import { useEffect, useMemo, useState } from 'react';
import { addYears, endOfMonth, endOfYear, startOfMonth, startOfYear } from 'date-fns';
import { Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { VIEWING_OTHER_RESOURCE_TITLE } from './weekNavTypes';

/** Years ahead the year dropdown reaches when future-dated entries are unlimited. */
const UNLIMITED_FUTURE_YEARS_AHEAD = 5;

export interface WeekNavCalendarPopoverProps {
  currentWeekStart: Date;
  minAllowedDate: Date;
  maxAllowedDate: Date | undefined;
  isViewingOtherResource: boolean;
  onNavigateToDate: (date: Date) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/** Date picker popover shared by the desktop, tablet and mobile week selectors. */
export function WeekNavCalendarPopover({
  currentWeekStart,
  minAllowedDate,
  maxAllowedDate,
  isViewingOtherResource,
  onNavigateToDate,
  open,
  onOpenChange,
}: WeekNavCalendarPopoverProps) {
  // The month the picker displays. Held here so the month/year dropdowns move the
  // grid, and reset to the week in view whenever the popover reopens.
  const [month, setMonth] = useState<Date>(currentWeekStart);

  useEffect(() => {
    setMonth(currentWeekStart);
  }, [currentWeekStart]);

  // The window the month/year dropdowns can navigate between. Without an explicit
  // range react-day-picker only offers the current year, so the year dropdown
  // cannot leave it. The span covers the entry window the organisation allows and
  // is stretched to include the week currently in view.
  const navMonths = useMemo(() => {
    const start = startOfMonth(minAllowedDate);
    const end = maxAllowedDate
      ? endOfMonth(maxAllowedDate)
      : endOfYear(addYears(new Date(), UNLIMITED_FUTURE_YEARS_AHEAD));
    return {
      startMonth: currentWeekStart < start ? startOfYear(currentWeekStart) : start,
      endMonth: currentWeekStart > end ? endOfYear(currentWeekStart) : end,
    };
  }, [currentWeekStart, minAllowedDate, maxAllowedDate]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) setMonth(currentWeekStart);
    onOpenChange?.(nextOpen);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          aria-label="Pick a date"
          disabled={isViewingOtherResource}
          title={isViewingOtherResource ? VIEWING_OTHER_RESOURCE_TITLE : undefined}
        >
          <Calendar className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <CalendarComponent
          mode="single"
          selected={currentWeekStart}
          onSelect={(date) => {
            if (date) onNavigateToDate(date);
          }}
          disabled={(date) => {
            if (date < minAllowedDate) return true;
            if (maxAllowedDate && date > maxAllowedDate) return true;
            return false;
          }}
          month={month}
          onMonthChange={setMonth}
          captionLayout="dropdown"
          startMonth={navMonths.startMonth}
          endMonth={navMonths.endMonth}
          autoFocus
        />
      </PopoverContent>
    </Popover>
  );
}
